import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Loader2, AlertTriangle, Trash2, Plus, Check, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, gbp } from "../lib/api";

const UNITS = ["kg", "g", "L", "ml", "each"];
const inputCls = "h-10 px-2 rounded-md border border-sand bg-white text-sm focus:outline-none focus:ring-2 focus:ring-tea-hover/70 w-full";
const VOICE_MIME_CANDIDATES = ["audio/webm", "audio/mp4"]; // Chrome/Opus, then Safari/AAC
const VOICE_MAX_SECONDS = 90;

export default function CapturePage() {
  const [phase, setPhase] = useState("upload");
  const [scanMode, setScanMode] = useState("receipt"); // "receipt" | "voice" — only affects the scanning-phase copy
  const [draft, setDraft] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const fileRef = useRef();
  const navigate = useNavigate();
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => stopMediaStream(), []);
  useEffect(() => {
    api.get("/ingredients").then((response) => setIngredients(response.data)).catch(() => {});
  }, []);

  const stopMediaStream = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Flagged lines (no printed weight/count) that match an ingredient with a remembered
  // correction get their quantity/unit prefilled from it. `_flaggedQty` is fixed at draft
  // creation and (unlike `flagged`) never cleared by editing, so the "remember" checkbox
  // stays put even after the user types over the prefilled value.
  //
  // Money invariant for these lines: the till printed a price with no weight/count, so
  // line_total is the one real fact (money actually paid) and unit_price was only ever a
  // guess (line_total / the extractor's default quantity of 1). Prefilling a remembered
  // quantity must hold line_total fixed and derive unit_price from it, never the reverse.
  const prepLines = (rawLines) =>
    rawLines.map((l) => {
      const prefill = l.flagged && l.assumed_quantity != null;
      const quantity = prefill ? l.assumed_quantity : l.quantity;
      const unit = prefill && l.assumed_unit ? l.assumed_unit : l.unit;
      const line_total = l.line_total;
      const unit_price = prefill && quantity > 0
        ? Math.round((line_total / quantity) * 10000) / 10000
        : l.unit_price;
      return {
        ...l,
        _key: crypto.randomUUID(),
        _flaggedQty: !!l.flagged,
        quantity,
        unit,
        unit_price,
        line_total,
        remember: !!l.flagged,
      };
    });

  const onFile = async (file) => {
    if (!file) return;
    setScanMode("receipt");
    setPhase("scanning");
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/receipts/scan", form);
      setDraft({ ...data, lines: prepLines(data.lines) });
      setPhase("confirm");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPhase("upload");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = VOICE_MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => onRecordingStop(mimeType);
      recorderRef.current = recorder;
      recorder.start();
      setRecordSeconds(0);
      setPhase("recording");
      timerRef.current = setInterval(() => {
        setRecordSeconds((s) => {
          const next = s + 1;
          if (next >= VOICE_MAX_SECONDS) stopRecording();
          return next;
        });
      }, 1000);
    } catch (err) {
      toast.error("Couldn't access the microphone. Check your browser permissions.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    recorderRef.current?.stop();
  };

  const cancelRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recorderRef.current) {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    stopMediaStream();
    setPhase("upload");
  };

  const onRecordingStop = async (mimeType) => {
    stopMediaStream();
    const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
    const filename = mimeType.includes("mp4") ? "voice-memo.mp4" : "voice-memo.webm";
    setScanMode("voice");
    setPhase("scanning");
    const form = new FormData();
    form.append("file", blob, filename);
    try {
      const { data } = await api.post("/purchases/voice-scan", form);
      setDraft({ ...data, lines: prepLines(data.lines) });
      setPhase("confirm");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPhase("upload");
    }
  };

  const setLine = (i, key, value) => {
    const lines = [...draft.lines];
    const wasFlaggedQty = lines[i]._flaggedQty; // true for a line that came in with no printed weight/count
    lines[i] = { ...lines[i], [key]: value, flagged: false };
    if (wasFlaggedQty && (key === "quantity" || key === "unit_price")) {
      // The printed total is the one fact we actually have for these lines (till showed a
      // price, no weight/count) — hold it fixed and derive whichever of qty/unit_price the
      // user didn't just type, instead of letting the total drift.
      const total = parseFloat(lines[i].line_total) || 0;
      if (key === "quantity") {
        const q = parseFloat(value) || 0;
        if (q > 0) lines[i].unit_price = Math.round((total / q) * 10000) / 10000;
      } else {
        const up = parseFloat(value) || 0;
        if (up > 0) lines[i].quantity = Math.round((total / up) * 10000) / 10000;
      }
    } else if (key === "quantity" || key === "unit_price") {
      const q = parseFloat(key === "quantity" ? value : lines[i].quantity) || 0;
      const up = parseFloat(key === "unit_price" ? value : lines[i].unit_price) || 0;
      lines[i].line_total = Math.round(q * up * 100) / 100;
    }
    setDraft({ ...draft, lines });
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const payload = {
        supplier_name: draft.supplier_name ?? "",
        purchased_at: draft.purchased_at,
        total_amount: parseFloat(draft.total_amount) || 0,
        lines: draft.lines.map((l) => ({
          name: l.name, quantity: parseFloat(l.quantity) || 0, unit: l.unit,
          unit_price: parseFloat(l.unit_price) || 0, line_total: parseFloat(l.line_total) || 0, flagged: false,
          remember: !!l.remember,
          product_id: l.product_id || null,
        })),
      };
      const { data } = await api.post(`/purchases/${draft.id}/confirm`, payload);
      toast.success(`${data.line_count} items saved to your ingredient store`);
      data.alerts.forEach((a) =>
        toast.warning(`${a.ingredient_name} ${a.pct_change > 0 ? "up" : "down"} ${Math.abs(a.pct_change)}% since last purchase`));
      navigate("/ingredients");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (phase === "recording")
    return (
      <div className="py-24 flex flex-col items-center gap-6 animate-rise" data-testid="recording-state">
        <span className="w-20 h-20 rounded-full bg-tea text-tea-text flex items-center justify-center animate-pulse">
          <Mic size={32} />
        </span>
        <div className="text-center">
          <p className="font-heading font-semibold text-xl">Listening…</p>
          <p className="text-sm text-ink/50 mt-1 font-mono-num">
            {String(Math.floor(recordSeconds / 60)).padStart(2, "0")}:{String(recordSeconds % 60).padStart(2, "0")} / 01:30
          </p>
        </div>
        <div className="flex gap-3">
          <button data-testid="stop-recording-button" onClick={stopRecording}
            className="h-12 px-6 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium flex items-center justify-center gap-2 transition-colors duration-200">
            <Square size={16} /> Done
          </button>
          <button data-testid="cancel-recording-button" onClick={cancelRecording}
            className="h-12 px-5 rounded-lg border border-sand text-ink/60 hover:bg-sand transition-colors duration-200">
            Cancel
          </button>
        </div>
      </div>
    );

  if (phase === "scanning")
    return (
      <div className="py-24 flex flex-col items-center gap-4 animate-rise" data-testid="scanning-state">
        <Loader2 className="animate-spin text-tea-text" size={40} />
        <p className="font-heading font-semibold text-xl">{scanMode === "voice" ? "Listening to your memo…" : "Reading your receipt…"}</p>
        <p className="text-sm text-ink/50">Extracting items, quantities and prices.</p>
      </div>
    );

  if (phase === "confirm" && draft)
    return (
      <div className="space-y-6 animate-rise" data-testid="confirm-screen">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">One-tap confirm</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-1">Check what we found</h1>
          {draft.lines.some((l) => l.flagged) && (
            <p className="mt-2 text-sm text-warnamber flex items-center gap-1.5">
              <AlertTriangle size={15} /> Amber rows are low-confidence. A quick glance is enough.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Supplier</label>
            <input data-testid="confirm-supplier-input" className={`${inputCls} mt-1`} value={draft.supplier_name ?? ""}
              onChange={(e) => setDraft({ ...draft, supplier_name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Date</label>
            <input type="date" data-testid="confirm-date-input" className={`${inputCls} mt-1`} value={draft.purchased_at}
              onChange={(e) => setDraft({ ...draft, purchased_at: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Receipt total (£)</label>
            <input type="number" step="0.01" data-testid="confirm-total-input" className={`${inputCls} mt-1 font-mono-num`} value={draft.total_amount ?? ""}
              onChange={(e) => setDraft({ ...draft, total_amount: e.target.value })} />
          </div>
        </div>

        <div className="space-y-3" data-testid="confirm-lines">
          {draft.lines.map((line, i) => (
            <div key={line._key} data-testid={`confirm-line-${i}`}
              className={`rounded-lg border p-3 ${line.flagged ? "border-warnamber bg-warnamber/10" : "border-sand bg-white"}`}>
              <div className="flex items-center gap-2">
                <input data-testid={`line-name-${i}`} className={inputCls} value={line.name} placeholder="Ingredient name"
                  onChange={(e) => setLine(i, "name", e.target.value)} />
                <button data-testid={`line-delete-${i}`} className="p-2 text-ink/40 hover:text-tea-text shrink-0"
                  onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, j) => j !== i) })}>
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <input type="number" step="any" data-testid={`line-qty-${i}`} className={`${inputCls} font-mono-num`} value={line.quantity}
                  onChange={(e) => setLine(i, "quantity", e.target.value)} placeholder="Qty" />
                <select data-testid={`line-unit-${i}`} className={inputCls} value={line.unit} onChange={(e) => setLine(i, "unit", e.target.value)}>
                  {UNITS.map((u) => <option key={u}>{u}</option>)}
                </select>
                <input type="number" step="any" data-testid={`line-price-${i}`} className={`${inputCls} font-mono-num`} value={line.unit_price}
                  onChange={(e) => setLine(i, "unit_price", e.target.value)} placeholder="£/unit" />
                <div className="h-10 flex items-center justify-end font-mono-num text-sm font-medium pr-1">{gbp(line.line_total)}</div>
              </div>
              {line._flaggedQty && (
                <label className="mt-2 flex items-center gap-2 text-xs text-ink/60" data-testid={`line-remember-label-${i}`}>
                  <input type="checkbox" data-testid={`line-remember-${i}`} checked={!!line.remember}
                    onChange={(e) => {
                      const lines = [...draft.lines];
                      lines[i] = { ...lines[i], remember: e.target.checked };
                      setDraft({ ...draft, lines });
                    }} />
                  Remember this for {line.name || "this ingredient"} next time
                </label>
              )}
              {ingredients.some((ingredient) => ingredient.products?.length) && (
                <details className="mt-2 text-xs text-ink/60" data-testid={`line-product-map-${i}`}>
                  <summary className="min-h-10 flex items-center cursor-pointer font-medium text-tea-text">Map to a product</summary>
                  <select className={`${inputCls} mt-1`} value={line.product_id || ""}
                    onChange={(e) => setLine(i, "product_id", e.target.value)}>
                    <option value="">Use ingredient name matching</option>
                    {ingredients.filter((ingredient) => ingredient.products?.length).map((ingredient) => (
                      <optgroup key={ingredient.id} label={ingredient.name}>
                        {ingredient.products.map((product) => (
                          <option key={product.id} value={product.id}>{product.name} ({gbp(product.latest_unit_price)}/{ingredient.canonical_unit})</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </details>
              )}
            </div>
          ))}
        </div>

        <button data-testid="add-line-button" onClick={() => setDraft({ ...draft, lines: [...draft.lines, { _key: crypto.randomUUID(), name: "", quantity: 1, unit: "each", unit_price: 0, line_total: 0, flagged: false }] })}
          className="flex items-center gap-2 text-sm text-almond font-medium hover:underline">
          <Plus size={16} /> Add a line
        </button>

        <div className="flex gap-3 pt-2">
          <button data-testid="confirm-purchase-button" onClick={confirm} disabled={busy}
            className="flex-1 h-12 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} Confirm purchase
          </button>
          <button data-testid="discard-scan-button" onClick={() => { api.delete(`/purchases/${draft.id}`); setDraft(null); setPhase("upload"); }}
            className="h-12 px-5 rounded-lg border border-sand text-ink/60 hover:bg-sand transition-colors duration-200">
            Discard
          </button>
        </div>
      </div>
    );

  return (
    <div className="space-y-6 animate-rise" data-testid="capture-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Zero-friction capture</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">Snap a receipt</h1>
        <p className="text-ink/60 mt-2 text-sm sm:text-base">Photo → items, prices and supplier extracted for you. Never a blank form.</p>
      </div>
      <button data-testid="receipt-upload-button" onClick={() => fileRef.current?.click()}
        className="w-full rounded-lg border-2 border-dashed border-clay/50 bg-surface hover:border-tea-hover hover:bg-tea/10 transition-colors duration-200 p-12 sm:p-20 flex flex-col items-center gap-4">
        <span className="w-16 h-16 rounded-full bg-tea text-tea-text flex items-center justify-center"><Camera size={28} /></span>
        <div className="text-center">
          <p className="font-heading font-semibold text-xl">Take a photo or upload</p>
          <p className="text-sm text-ink/50 mt-1">JPEG, PNG, WEBP or PDF · use a till photo, app screenshot, exported order confirmation, or Amazon-style order-history PDF</p>
        </div>
      </button>
      <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden"
        data-testid="receipt-file-input" onChange={(e) => onFile(e.target.files?.[0])} />

      <div className="flex items-center gap-3 text-ink/40">
        <div className="flex-1 h-px bg-sand" />
        <span className="text-xs uppercase tracking-[0.2em]">or</span>
        <div className="flex-1 h-px bg-sand" />
      </div>

      <button data-testid="voice-record-button" onClick={startRecording}
        className="w-full rounded-lg border border-sand bg-white hover:border-almond-hover hover:bg-almond/10 transition-colors duration-200 p-6 flex items-center gap-4">
        <span className="w-12 h-12 rounded-full bg-almond text-almond-text flex items-center justify-center shrink-0"><Mic size={20} /></span>
        <div className="text-left">
          <p className="font-heading font-semibold">No receipt or photo? No problem, just talk</p>
          <p className="text-sm text-ink/50 mt-0.5">"10kg chicken, £4.50 a kilo" · just say it, we'll do the rest</p>
        </div>
      </button>
    </div>
  );
}
